import React from "react";
import { Link } from "react-router-dom";

import { Chip, Skeleton, Text } from "@performyard/py-components";
import type { PYColumnDef } from "@performyard/py-components";
import type { WorkflowTodoListItem, WorkflowTodoStatus } from "@performyard/service-contracts";

import { appendCurrentLocationAsFrom } from "../../../utils/workflowsBreadcrumbs";

import { type SortDirection, WorkflowsTableShell, isLoadingRow } from "../WorkflowsTableShell";
import { formatUtcDate } from "../dateFormatting";

const STATUS_CHIP_CONFIG: Record<WorkflowTodoStatus, { label: string; color: "primary" | "green" | "red" }> = {
	active: { label: "Active", color: "primary" },
	completed: { label: "Completed", color: "green" },
	overdue: { label: "Overdue", color: "red" },
};

type AllTodosTableProps = {
	todos: WorkflowTodoListItem[];
	isLoading: boolean;
	onSortChange: (sort: { name: string; direction: SortDirection }) => void;
	activeSort: string;
	activeSortDirection: SortDirection;
	emptyStateText: string;
};

const AllTodosTable = ({
	todos,
	isLoading,
	onSortChange,
	activeSort,
	activeSortDirection,
	emptyStateText,
}: AllTodosTableProps) => {
	const columns: PYColumnDef<WorkflowTodoListItem>[] = [
		{
			colId: "action",
			headerName: "To Do",
			flex: 1,
			minWidth: 200,
			sortable: true,
			cellRenderer: (params: { data: WorkflowTodoListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-1">{row.action}</span>
					</Text>
				);
			},
		},
		{
			colId: "subject",
			headerName: "Assignee",
			type: "employee",
			width: 180,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.subject ?? null,
		},
		{
			colId: "workflow",
			headerName: "Workflow",
			flex: 1,
			minWidth: 180,
			cellRenderer: (params: { data: WorkflowTodoListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				if (!row.workflow) {
					return (
						<Text size="paragraph-3" weight="regular">
							—
						</Text>
					);
				}
				return (
					<Link
						to={appendCurrentLocationAsFrom(`/company/workflows/${row.workflow._id}`)}
						className="text-primary-5 no-underline hover:text-primary-7 hover:underline"
					>
						<Text size="paragraph-3" weight="regular">
							<span className="py-line-clamp-1">{row.workflow.name}</span>
						</Text>
					</Link>
				);
			},
		},
		{
			colId: "actor",
			headerName: "Actor",
			type: "employee",
			width: 180,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.actor ?? null,
		},
		{
			colId: "due_date",
			headerName: "Due Date",
			type: "date",
			width: 140,
			sortable: true,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.dueDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatUtcDate(params.value),
		},
		{
			colId: "date_done",
			headerName: "Completed Date",
			type: "date",
			width: 160,
			sortable: true,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.completedDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatUtcDate(params.value),
		},
		{
			colId: "status",
			headerName: "Status",
			width: 130,
			cellRenderer: (params: { data: WorkflowTodoListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-16" />;
				const { label, color } = STATUS_CHIP_CONFIG[row.status];
				return <Chip label={label} color={color} size={24} />;
			},
		},
	];

	return (
		<WorkflowsTableShell<WorkflowTodoListItem>
			testId="todos-table"
			columns={columns}
			rows={todos}
			isLoading={isLoading}
			onSortChange={onSortChange}
			activeSort={activeSort}
			activeSortDirection={activeSortDirection}
			emptyStateText={emptyStateText}
		/>
	);
};

export default AllTodosTable;
